import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogIn, LogOut, User, Copy, Share2, Trash2, ChevronRight, Gift, Users, Palette, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSavedAddresses, saveCustomerProfile } from "@/hooks/useLocal";
import { useShop } from "@/context/ShopContext";
import LoyaltyCard from "@/components/LoyaltyCard";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

function MilestoneCard({ loyalty }) {
  const target = Number(loyalty.monthly_milestone_target || 0);
  const spend = Number(loyalty.month_spend || 0);
  const remaining = Math.max(target - spend, 0);
  const pct = target > 0 ? Math.min(100, Math.round((spend / target) * 100)) : 0;
  const rows = [
    ["Earned points", loyalty.points - (loyalty.bonus_points || 0) - (loyalty.milestone_points || 0), ""],
    ["Game points", loyalty.bonus_points || 0, loyalty.bonus_points_expires_at],
    ["Milestone points", loyalty.milestone_points || 0, loyalty.milestone_points_expires_at],
  ];
  return (
    <div className="rounded-2xl border border-border p-5" data-testid="milestone-card">
      <h2 className="font-display text-sm font-bold">Rewards &amp; milestone</h2>
      {target > 0 ? (
        <div className="mt-3">
          <p className="text-sm">{loyalty.milestone_hit_this_month ? "Milestone reached this month — bonus points unlocked! 🎉" : `Spend ${money(remaining)} more this month to hit your ${money(target)} milestone and double this month's points.`}</p>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="milestone-progress" />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{money(spend)} of {money(target)} ({pct}%)</p>
        </div>
      ) : null}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {rows.map(([label, val, exp]) => (
          <div key={label} className="rounded-xl bg-secondary p-3 text-center">
            <p className="font-display text-lg font-bold">{Math.max(0, val)}</p>
            <p className="text-[11px] text-muted-foreground">{label}</p>
            {exp ? <p className="mt-0.5 text-[10px] text-muted-foreground">exp {dayjs(exp).format("D MMM")}</p> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading, signIn, signOut, setUser } = useAuth();
  const { addresses, remove } = useSavedAddresses();
  const { shop, theme } = useShop();
  const [phone, setPhone] = useState("");
  const [friendCode, setFriendCode] = useState("");

  useEffect(() => {
    if (user) setPhone(user.phone);
  }, [user]);

  const loyaltyQuery = useQuery({ queryKey: ["loyalty", user?.phone ?? ""], queryFn: () => api.getLoyalty(user.phone), enabled: !!user?.phone });

  const savePhone = useMutation({
    mutationFn: () => api.updateProfile(phone.replace(/\D/g, ""), user?.name),
    onSuccess: (u) => {
      setUser(u);
      saveCustomerProfile({ name: u.name, phone: u.phone });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast.success("Phone number saved");
    },
    onError: (e) => toast.error(e.message || "Could not save"),
  });

  const claimReferral = useMutation({
    mutationFn: () => api.claimReferral(friendCode.trim().toUpperCase(), user.phone),
    onSuccess: () => {
      setFriendCode("");
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      toast.success("Welcome offer unlocked! Book your first order to claim it");
    },
    onError: (e) => toast.error(e.message || "Couldn't apply code"),
  });

  const loyalty = loyaltyQuery.data;
  const phoneDirty = !!user && phone.replace(/\D/g, "") !== user.phone;
  const canClaim = !!loyalty && !loyalty.referred_by && loyalty.completed_orders === 0 && !!user?.phone;
  const refCode = loyalty?.referral_code || "";

  const shareRef = () => {
    const text = `Join me at ${shop?.name || "Dry Cleaners Goldys"}! Use my code ${refCode} for a welcome offer.`;
    if (navigator.share) navigator.share({ title: "Goldys referral", text }).catch(() => {});
    else {
      navigator.clipboard?.writeText(text);
      toast.success("Referral message copied");
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-4">
      <h1 className="font-display text-2xl font-bold">Account</h1>

      {loading ? null : !user ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-secondary p-8 text-center" data-testid="profile-signed-out">
          <User className="h-11 w-11 text-primary" />
          <p className="font-display text-lg font-bold">You&apos;re browsing as a guest</p>
          <p className="text-sm text-muted-foreground">Sign in to book pickups, track orders and earn Goldys Rewards.</p>
          <button onClick={signIn} data-testid="profile-signin-button" className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
            <LogIn className="h-4 w-4" /> Sign in with Google
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 rounded-2xl border border-border p-4" data-testid="profile-identity">
            {user.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--brand-tertiary))] text-primary"><User className="h-6 w-6" /></div>}
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-base font-semibold">{user.name || "Goldys customer"}</p>
              <p className="truncate text-sm text-muted-foreground">{user.email}</p>
            </div>
            <button onClick={signOut} data-testid="profile-signout" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
          </div>

          <div>
            <label className="text-sm font-semibold">Mobile number</label>
            <p className="text-xs text-muted-foreground">Used for pickup coordination and your rewards balance.</p>
            <div className="mt-2 flex gap-2">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" placeholder="10-digit mobile" data-testid="profile-phone-input" className="flex-1 rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
              <button onClick={() => savePhone.mutate()} disabled={!phoneDirty || phone.replace(/\D/g, "").length < 7 || savePhone.isPending} data-testid="profile-phone-save" className="rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50">Save</button>
            </div>
          </div>

          {loyalty ? <LoyaltyCard loyalty={loyalty} /> : null}
          {loyalty ? <MilestoneCard loyalty={loyalty} /> : null}

          {/* Points & Wallet */}
          <Link to="/wallet" className="flex items-center justify-between rounded-2xl border border-border p-4" data-testid="profile-wallet-link">
            <span className="flex items-center gap-2 text-sm font-semibold"><Wallet className="h-4 w-4 text-primary" /> Points &amp; Wallet</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>

          {/* App colour / skin */}
          <ThemePicker user={user} setUser={setUser} theme={theme} />

          {/* Referral */}
          {refCode ? (
            <div className="rounded-2xl border border-border p-5">
              <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /><h2 className="font-display text-sm font-bold">Refer & earn</h2></div>
              <p className="mt-1 text-xs text-muted-foreground">Share your code — your friend gets a welcome offer and you earn {shop?.referral_bonus_points || 0} points per referral.</p>
              <div className="mt-3 flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
                <span className="font-display text-lg font-bold tracking-wider">{refCode}</span>
                <div className="flex gap-2">
                  <button onClick={() => { navigator.clipboard?.writeText(refCode); toast.success("Code copied"); }} className="rounded-full bg-card p-2"><Copy className="h-4 w-4" /></button>
                  <button onClick={shareRef} className="rounded-full bg-primary p-2 text-primary-foreground"><Share2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Claim friend code */}
          {canClaim ? (
            <div className="rounded-2xl border border-border p-5">
              <h2 className="font-display text-sm font-bold">Got a friend&apos;s code?</h2>
              <div className="mt-2 flex gap-2">
                <input value={friendCode} onChange={(e) => setFriendCode(e.target.value)} placeholder="Enter referral code" className="flex-1 rounded-xl border border-border bg-card p-3 text-sm uppercase outline-none focus:border-primary" />
                <button onClick={() => claimReferral.mutate()} disabled={!friendCode.trim() || claimReferral.isPending} className="rounded-xl border border-primary px-5 text-sm font-semibold text-primary disabled:opacity-50">Apply</button>
              </div>
            </div>
          ) : null}

          {/* Community discount */}
          <Link to="/community-discount" className="flex items-center justify-between rounded-2xl border border-border p-4">
            <span className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-primary" /> Community discount</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>

          {/* Saved addresses */}
          {addresses.length > 0 ? (
            <div className="rounded-2xl border border-border p-5">
              <h2 className="mb-3 font-display text-sm font-bold">Saved addresses</h2>
              <div className="space-y-2">
                {addresses.map((a) => (
                  <div key={a.id} className="flex items-start justify-between gap-2 rounded-xl bg-secondary p-3">
                    <div><p className="text-sm font-semibold">{a.label}</p><p className="text-xs text-muted-foreground">{a.address}</p></div>
                    <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}

      <div className="flex items-center justify-between pt-2 text-sm">
        <Link to="/terms" className="font-semibold text-primary">Terms & Care policy</Link>
        <Link to="/owner" className="text-muted-foreground">Owner portal</Link>
      </div>
    </div>
  );
}

function ThemePicker({ user, setUser, theme }) {
  const presets = theme?.presets ?? [];
  const [custom, setCustom] = useState(user?.personal_theme_primary || theme?.primary || "#2E5C45");
  const activeKey = user?.personal_theme_preset_key || null;

  const apply = (preset_key, primary, accent) =>
    api.setPersonalTheme(preset_key, primary, accent)
      .then((u) => { setUser(u); toast.success("App colour updated"); })
      .catch((e) => toast.error(e.message || "Could not update"));

  const reset = () =>
    api.resetPersonalTheme().then((u) => { setUser(u); toast.success("Reset to shop default"); }).catch((e) => toast.error(e.message));

  return (
    <div className="rounded-2xl border border-border p-5" data-testid="theme-picker">
      <div className="flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /><h2 className="font-display text-sm font-bold">Change app colour</h2></div>
      <p className="mt-1 text-xs text-muted-foreground">Personalise your app&apos;s look — applies to your account.</p>
      {presets.length ? (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {presets.map((p) => (
            <button key={p.key} onClick={() => apply(p.key, p.primary, p.accent)} data-testid={`skin-${p.key}`} className={cn("flex items-center gap-2 rounded-xl border p-3 text-left", activeKey === p.key ? "border-primary" : "border-border")}>
              <span className="flex gap-1"><span className="h-5 w-5 rounded-full" style={{ background: p.primary }} /><span className="h-5 w-5 rounded-full" style={{ background: p.accent }} /></span>
              <span className="text-xs font-semibold">{p.label}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-3 flex items-end gap-3">
        <label className="text-xs font-medium">Custom<input type="color" value={custom} onChange={(e) => setCustom(e.target.value)} className="mt-1 block h-9 w-14 rounded border border-border" /></label>
        <button onClick={() => apply("custom", custom, custom)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Apply custom</button>
        <button onClick={reset} className="text-xs font-semibold text-muted-foreground">Reset</button>
      </div>
    </div>
  );
}

